/**
 * RevenueFunnelDrillDrawer — REX-106/107 (Feb 2026)
 *
 * Opens when a funnel stage is clicked from RevenueSnapshot or RevenuePage.
 * Shows the underlying rows (opportunities for "pipeline", milestones for
 * "contracted/invoiced/paid") and lets the user click through to the
 * client (via the universal ClientDrawer) or to the engagement.
 */
import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Loader2, ExternalLink, Building2 } from "lucide-react";
import { useClientDrawer } from "./ClientDrawerProvider";
import { useNavigate } from "react-router-dom";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const STAGE_LABELS = {
  pipeline: "Weighted pipeline",
  contracted: "Contracted",
  invoiced: "Invoiced",
  paid: "Paid",
};

function fmtCurrency(v, ccy) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy || "GBP", maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${ccy || ""} ${v}`;
  }
}

export default function RevenueFunnelDrillDrawer({ open, onClose, stage, workspaceId, period, currency, onOpportunityChanged }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [actingId, setActingId] = useState(null);
  const { openClient } = useClientDrawer();
  const navigate = useNavigate();

  // Recompute the summary locally after a status change so the drawer's
  // header reflects the new total without waiting for a parent refetch.
  const recomputeSummary = (remainingRows) => {
    if (!remainingRows) return;
    const withVal = remainingRows.filter((r) => r.has_value);
    const total_weighted = withVal.reduce((s, r) => s + (r.amount || 0), 0);
    const total_raw = withVal.reduce((s, r) => s + (r.raw_amount || 0), 0);
    setSummary((prev) => ({
      ...(prev || {}),
      total_weighted,
      total_raw,
      count_with_value: withVal.length,
      count_without_value: remainingRows.length - withVal.length,
      count_total: remainingRows.length,
    }));
  };

  // Pause a LIVE deal. Status is derived — we never set it directly; on_hold is the one
  // orthogonal flag. A held deal's derived status becomes "On Hold" and drops out of this
  // (Active-only) list. Closing a deal is NOT done here — it goes through the one close
  // control on the deal itself (the four endings), which asks the ending and a reason.
  const putOnHold = async (rowId) => {
    setActingId(rowId);
    try {
      const res = await fetch(`${API}/opportunities/${rowId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ on_hold: true }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        alert(b.detail || `Failed (${res.status})`);
        return;
      }
      const remaining = rows.filter((x) => x.id !== rowId);
      setRows(remaining);
      recomputeSummary(remaining);
      if (onOpportunityChanged) onOpportunityChanged({ opportunity_id: rowId, new_status: "On Hold" });
      try {
        window.dispatchEvent(new CustomEvent("rex:opportunity-status-changed", {
          detail: { opportunity_id: rowId, new_status: "On Hold" },
        }));
      } catch { /* noop */ }
    } catch (err) {
      alert("Network error: " + (err.message || ""));
    } finally {
      setActingId(null);
    }
  };

  useEffect(() => {
    if (!open || !stage || !workspaceId) return;
    let cancel = false;
    async function load() {
      setLoading(true); setError(null); setRows([]); setSummary(null);
      try {
        if (stage === "pipeline") {
          // Pipeline drill uses the NEW unified endpoint — same source + same
          // weighted maths as the Revenue card, so totals reconcile exactly.
          const params = new URLSearchParams();
          if (currency) params.set("currency", currency);
          const r = await fetch(`${API}/v2/workspaces/${workspaceId}/revenue/pipeline/drill?${params}`, { credentials: "include" });
          if (!r.ok) throw new Error("pipeline_unavailable");
          const d = await r.json();
          const mapped = (d.rows || []).map((o) => ({
            kind: "opportunity",
            id: o.opportunity_id,
            title: o.opportunity_name,
            client_id: o.client_id,
            client_name: o.company_name,
            stage: o.stage,
            amount: o.weighted_amount,          // what the card sums to
            raw_amount: o.raw_amount,            // shown as sub-text
            stage_probability: o.stage_probability,
            currency: o.currency,
            has_value: o.has_value,
            hint: o.is_unconfirmed ? "Auto-created from a Morning Intelligence signal — confirm or edit." : null,
            source_signal: o.source_signal_headline || null,
          }));
          if (!cancel) {
            setRows(mapped);
            setSummary(d.summary || null);
          }
        } else {
          // Contracted / Invoiced / Paid → milestones
          const params = new URLSearchParams({ period: period || "this_quarter" });
          if (currency) params.set("currency", currency);
          const r = await fetch(`${API}/v2/workspaces/${workspaceId}/revenue?${params}`, { credentials: "include" });
          if (!r.ok) throw new Error("revenue_unavailable");
          const d = await r.json();
          const all = d.rows || [];
          const filtered = all
            .filter((m) => {
              const s = (m.status || "pending").toLowerCase();
              if (stage === "contracted") return true;
              if (stage === "invoiced") return s === "invoiced";
              if (stage === "paid") return s === "paid";
              return false;
            })
            .map((m) => ({
              kind: "milestone",
              id: m.milestone_id,
              title: m.title,
              client_id: m.client_id,
              client_name: m.client_name,
              engagement_id: m.engagement_id,
              engagement_name: m.engagement_name,
              status: m.status,
              amount: m.amount_target,
              currency: d.filters?.currency || "GBP",
              due_date: m.due_date,
            }))
            .sort((a, b) => (b.amount || 0) - (a.amount || 0));
          if (!cancel) setRows(filtered);
        }
      } catch (e) {
        if (!cancel) setError(e.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
  }, [open, stage, workspaceId, period, currency]);

  // Pipeline drill: totals come from the backend summary (single source of truth
  // matching the card). Other drill types still use the local sum.
  const isPipeline = stage === "pipeline";
  const totalFallback = rows.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalWeighted = isPipeline && summary ? summary.total_weighted : totalFallback;
  const totalRaw = isPipeline && summary ? summary.total_raw : null;
  const ccy = (isPipeline && summary ? summary.currency : null) || rows[0]?.currency || currency || "GBP";
  const handleClient = (cid) => { if (cid) { onClose(); openClient(cid); } };
  const handleEngagement = (eid) => { if (eid) { onClose(); navigate(`/engagements/${eid}`); } };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:w-[640px] sm:max-w-none p-0 overflow-y-auto" data-testid="revenue-drill-drawer">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <SheetTitle className="text-lg flex items-center gap-2">
            {STAGE_LABELS[stage] || stage}
            <Badge variant="outline" className="text-[10px] bg-white border-slate-300" data-testid="drill-count">
              {isPipeline && summary
                ? <>{summary.count_with_value} deal{summary.count_with_value === 1 ? "" : "s"}{summary.count_without_value > 0 && (
                    <span className="text-amber-700 font-semibold ml-1">· {summary.count_without_value} missing value</span>
                  )}</>
                : <>{rows.length} item{rows.length === 1 ? "" : "s"}</>
              }
            </Badge>
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-600">
            {stage === "pipeline"
              ? "Active commercial opportunities in qualifying stages, weighted by stage probability. Click any row to drill into the client."
              : `${stage === "paid" ? "Cleared cash" : stage === "invoiced" ? "Issued invoices" : "Committed milestones"} this period. Click a row to view the client or engagement.`}
          </SheetDescription>
          <div className="mt-1" data-testid="drill-total">
            <p className="text-sm font-semibold text-slate-900">
              Weighted total: {fmtCurrency(totalWeighted, ccy)}
            </p>
            {isPipeline && totalRaw != null && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                Raw pipeline value: {fmtCurrency(totalRaw, ccy)}
                {summary?.fx_missing && <span className="ml-1 text-amber-700">· some FX rates missing</span>}
              </p>
            )}
          </div>
        </SheetHeader>

        <div className="p-6 space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : error ? (
            <p className="text-sm text-rose-600">Failed to load: {error}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No items in this stage for the selected period.</p>
          ) : rows.map((r) => (
            <div key={r.id} className="rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition p-3" data-testid={`drill-row-${r.id}`}>
              <div className="flex items-baseline gap-2 flex-wrap">
                {r.kind === "opportunity" ? (
                  <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">{r.stage}</Badge>
                ) : (
                  <Badge variant="outline" className={`text-[10px] ${
                    r.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    r.status === "invoiced" ? "bg-blue-50 text-blue-700 border-blue-200" :
                    "bg-slate-50 text-slate-700 border-slate-200"
                  }`}>{r.status}</Badge>
                )}
                <button
                  type="button"
                  className="text-sm font-semibold text-slate-900 text-left hover:text-indigo-700 transition"
                  onClick={() => r.kind === "opportunity" ? handleClient(r.client_id) : handleEngagement(r.engagement_id)}
                  data-testid={`drill-row-title-${r.id}`}
                >{r.title}</button>
                <span className="ml-auto text-sm font-bold tabular-nums" data-testid={`drill-row-amt-${r.id}`}>
                  {r.has_value === false
                    ? <span className="text-amber-700 text-xs italic">no value set</span>
                    : fmtCurrency(r.amount, r.currency)}
                </span>
              </div>
              {isPipeline && r.has_value && (
                <p className="text-[10px] text-slate-500 mt-0.5 ml-[3.5rem]">
                  Raw {fmtCurrency(r.raw_amount, r.currency)} × {Math.round((r.stage_probability || 0) * 100)}% ({r.stage})
                </p>
              )}
              {/* Per-opportunity action buttons (May 2026) — clicking
                  removes the deal from the active pipeline & re-totals the
                  drawer header. Only "Active" deals contribute to the
                  weighted pipeline, so any of these actions drops the row. */}
              {r.kind === "opportunity" && r.id && (
                <div className="ml-[3.5rem] mt-1.5 flex flex-wrap items-center gap-1" data-testid={`drill-row-actions-${r.id}`}>
                  <button
                    type="button"
                    onClick={() => putOnHold(r.id)}
                    disabled={actingId === r.id}
                    className="text-[10px] px-2 py-0.5 rounded border transition disabled:opacity-50 bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
                    data-testid={`drill-row-action-${r.id}-on-hold`}
                    title="Pause this live deal — closing is done from the deal itself"
                  >
                    {actingId === r.id ? "…" : "Put on Hold"}
                  </button>
                </div>
              )}
              <div className="flex items-baseline gap-3 mt-1 text-xs text-slate-500">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-indigo-700 transition"
                  onClick={() => handleClient(r.client_id)}
                  data-testid={`drill-row-client-${r.id}`}
                  disabled={!r.client_id}
                >
                  <Building2 className="h-3 w-3" />
                  {r.client_name || "—"}
                </button>
                {r.kind === "milestone" && r.engagement_name && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-indigo-700 transition"
                    onClick={() => handleEngagement(r.engagement_id)}
                  >
                    <ExternalLink className="h-3 w-3" />
                    {r.engagement_name}
                  </button>
                )}
                {r.due_date && <span>due {r.due_date}</span>}
              </div>
              {r.hint && <p className="text-[11px] text-amber-700 mt-1 italic">{r.hint}</p>}
              {r.source_signal && <p className="text-[11px] text-slate-500 mt-1 italic">Signal: "{r.source_signal.slice(0, 100)}"</p>}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
